import { Plus, Minus, Trash2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useCart } from "../../context/CartContext";
import { useMemo } from "react";
import { getImageUrl } from "../../utils/imageUtils";

export const CartSheet = ({
  theme: customTheme,
  shippingRates: customShippingRates,
  onCheckout,
}) => {
  const {
    cart,
    cartOpen: open,
    setCartOpen: onOpenChange,
    cartSubtotal,
    updateQuantity,
    removeFromCart
  } = useCart();

  const theme = useMemo(() => customTheme || {
    primary_color: "#0f172a", // slate-900
    accent_color: "#d97706",  // amber-600
    text_color: "#1e293b",    // slate-800
  }, [customTheme]);

  const hasExplicitShippingRates = !!customShippingRates;

  const shippingRates = useMemo(() => customShippingRates || {
    shipping_enabled: true,
    base_shipping_rate: 0,
    free_shipping_threshold: 0
  }, [customShippingRates]);

  const shippingCost = useMemo(() => {
    if (!hasExplicitShippingRates) return null; // Unknown — calculated at checkout
    if (!shippingRates.shipping_enabled) return 0;
    if (shippingRates.free_shipping_threshold > 0 && cartSubtotal >= shippingRates.free_shipping_threshold) return 0;
    return shippingRates.base_shipping_rate || 0;
  }, [shippingRates, cartSubtotal, hasExplicitShippingRates]);

  const cartTotal = cartSubtotal + (shippingCost || 0);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle style={{ color: theme.primary_color }}>
            Shopping Cart ({cart.length} items)
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-4">
          {cart.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <p>Your cart is empty</p>
            </div>
          ) : (
            <div className="space-y-4">
              {cart.map((item) => (
                <div key={item.product_id} className="flex gap-4 p-3 bg-slate-50 rounded-lg">
                  <img
                    src={getImageUrl(item.image_url)}
                    alt={item.name}
                    className="w-20 h-20 object-contain bg-white rounded border border-slate-100 p-1"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate" style={{ color: theme.primary_color }}>
                      {item.name}
                    </h4>
                    {item.variant_name && (
                      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                        Variant: {item.variant_name}
                      </p>
                    )}

                    {/* Customizations display */}
                    {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                      <div className="mt-2 space-y-1.5 border-l-2 border-indigo-100 pl-2 py-0.5">
                        {Array.isArray(item.customizations) ? (
                          item.customizations.map((cust) => (
                            <div key={cust.id} className="text-[10px]">
                              <div className="flex justify-between font-bold text-slate-600">
                                <span>{cust.name}</span>
                                <span className="text-indigo-600">+${cust.total.toFixed(2)}</span>
                              </div>
                              {cust.fields.map((f, i) => (
                                <p key={i} className="text-slate-400 italic leading-tight">
                                  {f.label}: {f.value} {f.price_modifier > 0 && <span className="ml-1 text-[9px] not-italic font-medium text-slate-500">(+${f.price_modifier.toFixed(2)})</span>}
                                </p>
                              ))}
                            </div>
                          ))
                        ) : (
                          Object.entries(item.customizations).map(([label, val]) => (
                            <p key={label} className="text-[10px] text-slate-400 italic">
                              <span className="font-medium mr-1 text-slate-500">{label}:</span>
                              {val && typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))
                                ? new Date(val).toLocaleDateString()
                                : val === true ? 'Yes' : val}
                            </p>
                          ))
                        )}
                      </div>
                    )}

                    {/* Add-ons display */}
                    {item.selectedAddons && item.selectedAddons.length > 0 && (
                      <p className="text-[10px] text-amber-700 font-medium">
                        +{item.selectedAddons.length} Add-on(s)
                      </p>
                    )}

                    <p className="text-sm font-semibold mt-1" style={{ color: theme.accent_color }}>
                      ${(item.total_price || item.base_price || 0).toFixed(2)}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        variant="outline"
                        size="icon"
                        className="w-7 h-7"
                        onClick={() => updateQuantity(item.product_id, -1)}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-8 text-center text-sm">{item.quantity}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="w-7 h-7"
                        onClick={() => updateQuantity(item.product_id, 1)}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 ml-auto text-red-500 hover:text-red-600"
                        onClick={() => removeFromCart(item.product_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Cart Summary */}
        {cart.length > 0 && (
          <div className="border-t pt-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span className="font-medium">${cartSubtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600 flex items-center gap-1">
                <Truck className="w-4 h-4" /> Shipping
              </span>
              <span className="font-medium">
                {shippingCost === null ? (
                  <span className="text-slate-400 text-xs">Calculated at checkout</span>
                ) : shippingCost === 0 ? (
                  <span className="text-green-600">FREE</span>
                ) : (
                  `$${shippingCost.toFixed(2)}`
                )}
              </span>
            </div>
            {shippingRates.free_shipping_threshold && cartSubtotal < shippingRates.free_shipping_threshold && (
              <p className="text-xs text-slate-500">
                Add ${(shippingRates.free_shipping_threshold - cartSubtotal).toFixed(2)} more for free shipping!
              </p>
            )}
            <div className="flex justify-between text-lg font-bold border-t pt-3">
              <span>Total</span>
              <span style={{ color: theme.accent_color }}>${cartTotal.toFixed(2)}</span>
            </div>
            <Button
              className="w-full py-6 text-lg font-semibold"
              style={{ backgroundColor: theme.accent_color }}
              onClick={onCheckout}
            >
              Proceed to Checkout
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
